import React from 'react';
import { ArrowLeft, Monitor, Shield, Zap, Users, Clock, CheckCircle, Upload, Send, FileText, Building, Download, Mail, Building2, Archive, Gavel, Search, UserCheck, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { legalConnectServices, legalConnectFeatures, companyInfo } from '../mockData';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';

const iconMap = {
  Upload,
  Send,
  FileText,
  Building,
  Download,
  Mail,
  Building2,
  Archive,
  Gavel,
  Search
};

const AttorneyServicePage = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If contact section doesn't exist on this page, scroll to bottom
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is LegalConnect and how does it help law firms?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LegalConnect is a comprehensive litigation support platform that provides law firms with nationwide document management, certified eFiling services across multiple states, process serving, skip tracing, and court access. It integrates seamlessly with popular legal practice management tools like Clio, iManage, and NetDocuments for streamlined workflows. The platform offers SOC 2 security compliance and helps California clients execute legal support services nationwide."
        }
      },
      {
        "@type": "Question",
        "name": "Which states does LegalConnect support for eFiling?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LegalConnect offers court-certified eFiling technology for superior courts in multiple states, with comprehensive coverage of all 58 California superior courts. The platform is expanding to additional states beyond 2025, including Texas courts and other jurisdictions. LegalConnect provides nationwide litigation support capabilities for California-based law firms."
        }
      },
      {
        "@type": "Question",
        "name": "Does LegalConnect integrate with existing law firm software?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, LegalConnect seamlessly integrates with popular legal practice management software including Clio, iManage, and NetDocuments. This integration allows attorneys to eFile documents directly from firm matters, automatically sync court-stamped copies and billing data, and streamline litigation workflows without changing existing systems."
        }
      },
      {
        "@type": "Question",
        "name": "Is LegalConnect secure for sensitive legal documents?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, LegalConnect maintains enterprise-grade SOC 2 security compliance, ensuring complete confidentiality and privacy for sensitive legal documents. The platform uses encrypted transmission, secure access controls, and comprehensive data protection measures to safeguard client information and case files."
        }
      },
      {
        "@type": "Question",
        "name": "What services are available through LegalConnect?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LegalConnect provides 10+ service types including eFiling (civil, family, probate), process serving, skip tracing, document retrieval, court research, subpoena preparation, county recording, notary services, legal copying, and court reporting. All services are accessible through a single online portal with 24/7 platform access."
        }
      },
      {
        "@type": "Question",
        "name": "How do I get started with LegalConnect?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Getting started is easy - you can create an account online, schedule a personalized demo to see the platform in action, or email us at support@legaldocspecialist.com. We provide comprehensive onboarding support, training resources, and dedicated customer service to ensure smooth integration with your firm's workflows."
        }
      }
    ]
  };

  React.useEffect(() => {
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div>
      <SEOHelmet 
        title="Attorney Services | LegalConnect | Legal Document Specialist California"
        description="Professional attorney services through LegalConnect platform. Comprehensive legal document management, e-filing, process serving, and case management for law firms in California."
        keywords="attorney services California, LegalConnect, legal document management, law firm services, e-filing attorneys, process serving law firms"
        canonical="/attorney-service"
      />
      <Header />
      
      {/* Hero Section */}
      <section style={{
        minHeight: '60vh',
        background: 'var(--bg-page)',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '160px',
        paddingBottom: '80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: 'radial-gradient(circle at 25% 25%, var(--brand-primary) 2px, transparent 2px)',
          backgroundSize: '60px 60px'
        }} />

        <div className="container">
          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Back Button */}
            <Link 
              to="/" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--brand-primary)',
                textDecoration: 'none',
                marginBottom: '2rem',
                fontSize: '1rem',
                fontWeight: '500'
              }}
              className="back-link"
            >
              <ArrowLeft size={20} />
              Back to Services
            </Link>

            {/* Hero Content */}
            <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'var(--bg-card)',
                padding: '0.75rem 1.5rem',
                borderRadius: '50px',
                marginBottom: '2rem',
                border: '1px solid var(--border-medium)'
              }}>
                <Monitor size={16} color="var(--brand-primary)" />
                <span className="caption" style={{ color: 'var(--text-primary)' }}>
                  LegalConnect Platform
                </span>
              </div>

              <h1 className="brand-display" style={{
                marginBottom: '1.5rem',
                fontSize: 'clamp(2.5rem, 4vw, 4rem)',
                textTransform: 'none'
              }}>
                Empower Your Firm with LegalConnect
              </h1>

              <p className="body-large" style={{
                marginBottom: '2rem',
                color: 'var(--text-secondary)',
                maxWidth: '700px',
                margin: '0 auto 2rem auto'
              }}>
                LegalConnect brings speed, accuracy, and control to your litigation support operations. From coast‑to‑coast court access and intuitive dashboards to rock‑solid security and seamless integration with your favorite legal tools, LegalConnect delivers a truly modern, end‑to‑end solution for order management, e‑filing, and process serving. We help California clients execute legal support services nationwide.
              </p>

              <div style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: '3rem',
                justifyContent: 'center'
              }}>
                <button 
                  onClick={scrollToContact}
                  className="btn-primary"
                  style={{ fontSize: '1.1rem', padding: '1.2em 2.5em' }}
                >
                  Request Demo
                </button>
                <button 
                  className="btn-secondary"
                  style={{ fontSize: '1.1rem', padding: '1.2em 2.5em' }}
                >
                  Learn More
                </button>
                <a 
                  href="https://legaldocspecialist.legalconnect.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ 
                    fontSize: '1.1rem', 
                    padding: '1.2em 2.5em', 
                    textDecoration: 'none', 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'var(--brand-primary)',
                    color: 'white',
                    border: '1px solid var(--brand-primary)'
                  }}
                >
                  Log In
                </a>
                <a 
                  href="https://legaldocspecialist.legalconnect.com/registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ 
                    fontSize: '1.1rem', 
                    padding: '1.2em 2.5em', 
                    textDecoration: 'none', 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'var(--brand-primary)',
                    color: 'white',
                    border: '1px solid var(--brand-primary)'
                  }}
                >
                  Create Account
                </a>
              </div>

              {/* Key Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '2rem',
                marginTop: '3rem'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div className="heading-2" style={{ 
                    color: 'var(--brand-primary)',
                    fontSize: '2.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    10+
                  </div>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Service Types
                  </p>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div className="heading-2" style={{ 
                    color: 'var(--brand-primary)',
                    fontSize: '2.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    24/7
                  </div>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Platform Access
                  </p>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div className="heading-2" style={{ 
                    color: 'var(--brand-primary)',
                    fontSize: '2.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    100%
                  </div>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Secure Platform
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '120px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Complete Legal Support Services
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Access all your litigation support needs through one powerful platform. We help California clients execute comprehensive legal services nationwide, streamlining workflows and improving efficiency across all 50 states.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {legalConnectServices.map((service) => {
              const IconComponent = iconMap[service.icon];
              
              return (
                <div 
                  key={service.id}
                  className="service-card"
                  style={{
                    backgroundColor: 'var(--bg-page)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.5rem',
                    padding: '2rem'
                  }}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <IconComponent size={28} color="var(--text-primary)" />
                  </div>

                  <div>
                    <h3 className="heading-3" style={{
                      marginBottom: '1rem',
                      fontSize: '1.3rem'
                    }}>
                      {service.title}
                    </h3>
                    
                    <p className="body-small" style={{
                      color: 'var(--text-secondary)',
                      lineHeight: '1.6'
                    }}>
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '120px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose LegalConnect?
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Elevate your efficiency and reliability with features designed specifically for modern law firms.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem'
          }}>
            {legalConnectFeatures.map((feature, index) => {
              const icons = [Monitor, Shield, Zap, Users, Clock, CheckCircle, UserCheck, TrendingUp];
              const IconComponent = icons[index % icons.length];
              
              return (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '1.5rem',
                    padding: '2rem',
                    backgroundColor: 'var(--bg-card)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-medium)',
                    transition: 'all 0.3s ease'
                  }}
                  className="feature-card"
                >
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <IconComponent size={24} color="var(--text-primary)" />
                  </div>
                  
                  <div>
                    <h3 className="heading-3" style={{
                      marginBottom: '1rem',
                      fontSize: '1.2rem'
                    }}>
                      {feature.title}
                    </h3>
                    
                    <p className="body-small" style={{
                      color: 'var(--text-secondary)',
                      lineHeight: '1.6'
                    }}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h3 className="heading-2" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Helpful Resources for Law Firms
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem'
            }}>
              {[
                {
                  title: 'Process Serving Guide',
                  description: 'Complete guide to California process serving requirements, procedures, and best practices.',
                  link: '/resources/process-serving-guide'
                },
                {
                  title: 'Court Procedures Guide',
                  description: 'Essential filing deadlines, service requirements, and 2025 legislative updates.',
                  link: '/resources/california-court-procedures-guide'
                },
                {
                  title: 'Legal Document Services',
                  description: 'Comprehensive overview of all legal document services for California law firms.',
                  link: '/resources/legal-document-services-guide'
                }
              ].map((resource, index) => (
                <Link 
                  key={index}
                  to={resource.link}
                  style={{
                    backgroundColor: 'var(--bg-page)',
                    padding: '2rem',
                    borderRadius: '12px',
                    border: '1px solid var(--border-medium)',
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'transform 0.2s, border-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'var(--border-medium)';
                  }}
                >
                  <h4 className="heading-4" style={{ marginBottom: '0.75rem', color: 'var(--brand-primary)' }}>
                    {resource.title}
                  </h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {resource.description}
                  </p>
                  <span style={{ 
                    display: 'inline-block',
                    marginTop: '1rem',
                    color: 'var(--brand-primary)',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>
                    Read Guide →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '120px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '3rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h2>

            {[
              {
                question: 'What is LegalConnect and how does it help law firms?',
                answer: 'LegalConnect is a comprehensive litigation support platform that provides law firms with nationwide document management, certified eFiling services across multiple states, process serving, skip tracing, and court access. It integrates seamlessly with popular legal practice management tools like Clio, iManage, and NetDocuments for streamlined workflows. The platform offers SOC 2 security compliance and helps California clients execute legal support services nationwide.'
              },
              {
                question: 'Which states does LegalConnect support for eFiling?',
                answer: 'LegalConnect offers court-certified eFiling technology for superior courts in multiple states, with comprehensive coverage of all 58 California superior courts. The platform is expanding to additional states beyond 2025, including Texas courts and other jurisdictions. LegalConnect provides nationwide litigation support capabilities for California-based law firms.'
              },
              {
                question: 'Does LegalConnect integrate with existing law firm software?',
                answer: 'Yes, LegalConnect seamlessly integrates with popular legal practice management software including Clio, iManage, and NetDocuments. This integration allows attorneys to eFile documents directly from firm matters, automatically sync court-stamped copies and billing data, and streamline litigation workflows without changing existing systems.'
              },
              {
                question: 'Is LegalConnect secure for sensitive legal documents?',
                answer: 'Yes, LegalConnect maintains enterprise-grade SOC 2 security compliance, ensuring complete confidentiality and privacy for sensitive legal documents. The platform uses encrypted transmission, secure access controls, and comprehensive data protection measures to safeguard client information and case files.'
              },
              {
                question: 'What services are available through LegalConnect?',
                answer: 'LegalConnect provides 10+ service types including eFiling (civil, family, probate), process serving, skip tracing, document retrieval, court research, subpoena preparation, county recording, notary services, legal copying, and court reporting. All services are accessible through a single online portal with 24/7 platform access.'
              },
              {
                question: 'How do I get started with LegalConnect?',
                answer: 'Getting started is easy - you can create an account online, schedule a personalized demo to see the platform in action, or email us at support@legaldocspecialist.com. We provide comprehensive onboarding support, training resources, and dedicated customer service to ensure smooth integration with your firm\'s workflows.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '1.5rem'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--bg-card)',
        padding: '120px 0'
      }}>
        <div className="container">
          <div style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <h2 className="heading-1" style={{ 
              marginBottom: '1.5rem',
              fontSize: 'clamp(2rem, 4vw, 3rem)'
            }}>
              Ready to Transform Your Practice?
            </h2>
            
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              marginBottom: '3rem',
              maxWidth: '600px',
              margin: '0 auto 3rem auto'
            }}>
              See LegalConnect in action today. Contact us to schedule a personalized demo and discover how we can streamline your litigation support operations.
            </p>
            
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={scrollToContact}
                className="btn-primary"
                style={{ fontSize: '1.1rem', padding: '1.2em 2.5em' }}
              >
                Schedule Demo
              </button>
              <a href="/quote"
                className="btn-secondary"
                style={{ fontSize: '1.1rem', padding: '1.2em 2.5em', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Request a Quote
              </a>
              <a 
                href="https://legaldocspecialist.legalconnect.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                style={{ fontSize: '1.1rem', padding: '1.2em 2.5em', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Log in
              </a>
              <a 
                href="https://legaldocspecialist.legalconnect.com/registration"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                style={{ fontSize: '1.1rem', padding: '1.2em 2.5em', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Create Account
              </a>
            </div>

            <div style={{
              marginTop: '3rem',
              padding: '2rem',
              backgroundColor: 'var(--bg-page)',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <p className="body-medium" style={{ 
                color: 'var(--text-primary)',
                marginBottom: '0.5rem'
              }}>
                Trusted by Law Firms Across California
              </p>
              <p className="caption" style={{ color: 'var(--text-secondary)' }}>
                Join hundreds of law firms already using LegalConnect
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .back-link:hover {
          color: var(--brand-hover) !important;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          background-color: #f7fafc !important;
          border-color: var(--brand-primary);
          box-shadow: 0 8px 25px rgba(44, 90, 160, 0.1);
        }

        @media (max-width: 767px) {
          .hero-content {
            text-align: center;
          }
          
          div[style*="gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))'"] {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AttorneyServicePage;